import styled from "styled-components";
import { useEffect, useRef, useState, useCallback } from "react";
import {
  createBasemaps,
  createLegendItems,
  createMapImageLayer,
  getGraphicsExtent,
  getLegendItemSublayers,
  getMapServiceInfo,
  getMapSublayer,
  LegendItem,
} from "../../utils/mapUtils";
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import Extent from "@arcgis/core/geometry/Extent";
import ScaleBar from "@arcgis/core/widgets/ScaleBar";
import BasemapGallery from "@arcgis/core/widgets/BasemapGallery";
import Expand from "@arcgis/core/widgets/Expand";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import mapConfig from "../../lib/mapConfig";
import MapLegend from "../../components/organisms/MapLegend";
import Search from "../../components/molecules/Search";
import { buildSearch } from "../../utils/searchUtils";
import MapLoadingSpinner from "../../components/atoms/MapLoadingSpinner";
import colours from "../../lib/colours";
import {
  useDrawingContext,
  useFeaturesContext,
  useMapContext,
  useMapMessagingContext,
  useMapModeContext,
  useMapScaleContext,
  useMiscellaneousContext,
  useNvrToolModeContext,
} from "../../context/ContextProvider";
import MapMessage from "./MapMessage";
import { IState } from "../../context/featuresReducer";
import GraphicsLayer from "esri/layers/GraphicsLayer";
import {
  getOverlappingFeatures,
  getPointBuffer,
  getPointLabel,
  getPolygonlabel,
} from "../../utils/featureUtils";
import Polygon from "@arcgis/core/geometry/Polygon";
import Point from "@arcgis/core/geometry/Point";
import Graphic from "@arcgis/core/Graphic";
import {
  Patch,
  Tree,
  featureTypes,
  mapAdditionalModes,
  mapModes,
  nvrToolModes,
  nvrTools,
  treeSize,
  treeType,
} from "../../lib/types";
import DialogMapContainer from "./DialogMapContainer";
import IdentifyButton from "../../components/organisms/IdentifyButton";
import Measurement from "@arcgis/core/widgets/Measurement.js";
import MeasureButton from "../../components/organisms/MeasureButton";
import DistanceMeasurement2D from "esri/widgets/DistanceMeasurement2D";
import appConfig from "../../lib/appConfig";

const MapOuterContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: ${colours.lightGrey};
`;

const MapDiv = styled.div`
  width: 100%;
  height: 100%;
`;

function MapContainer() {
  const { nvrToolMode }: { nvrToolMode: nvrToolModes } =
    useNvrToolModeContext();
  const {
    setMap,
    mapView,
    setMapView,
  }: { setMap: any; mapView: any; setMapView: any } = useMapContext();
  const state: IState = useFeaturesContext();
  const {
    setSketchVm,
    polygonGraphicsLayer,
    pointGraphicsLayer,
    treeBufferGraphicsLayer,
    labelGraphicsLayer,
  }: {
    setSketchVm: any;
    polygonGraphicsLayer: GraphicsLayer;
    pointGraphicsLayer: GraphicsLayer;
    treeBufferGraphicsLayer: GraphicsLayer;
    labelGraphicsLayer: GraphicsLayer;
  } = useDrawingContext();
  const {
    mapMode,
    setMapMode,
  }: {
    mapMode: mapModes;
    setMapMode: any;
  } = useMapModeContext();
  const { mapMessage, mapMessageType } = useMapMessagingContext();
  const { mapScale, setMapScale } = useMapScaleContext();
  const { uploading, setUploading } = useMiscellaneousContext();

  let sr: any = null;
  let mapSpinnerTimeout: any;

  const mapRef = useRef<any>();
  const searchKeyupListenerConfigured = useRef(false);
  const legendInfo = useRef<any>();
  const layersInfo = useRef<any>();
  const uploadingRef = useRef(false);
  const clickHandlerRef = useRef<any>();
  const measurementWidgetRef = useRef<any>();
  const mapModeRef = useRef<mapModes | undefined>();

  const [searchWidget, setSearchWidget] = useState<any>(null);
  const [mapViewUpdating, setMapViewUpdating] = useState(false);
  const [legendItems, setLegendItems] = useState<LegendItem[]>([]);
  const [identifiableLayer, setIdentifiableLayer] = useState<LegendItem>();
  const [searchClearVisible, setSearchClearVisible] = useState(false);
  const [identifyWindowOpen, setIdentifyWindowOpen] = useState(false);
  const [measureWindowOpen, setMeasureWindowOpen] = useState(false);
  const configureLayers = () => {
    getMapServiceInfo()
      .then(([lgdInfo, lyrsInfo]) => {
        legendInfo.current = lgdInfo;
        layersInfo.current = lyrsInfo;
        const mapImageLayer = createMapImageLayer(lgdInfo);
        mapRef.current.add(mapImageLayer);
        mapRef.current.add(polygonGraphicsLayer);
        mapRef.current.add(treeBufferGraphicsLayer);
        mapRef.current.add(pointGraphicsLayer);
        mapRef.current.add(labelGraphicsLayer);
        setLegendItems(
          createLegendItems(lyrsInfo.layers, lgdInfo.layers, mapRef.current)
        );
      })
      .catch((err) => {
        throw new Error(
          `There was a problem fetching layer information: ${err}`
        );
      });
  };

  const addMapViewUiComponents = (mView: any, basemaps: any[]) => {
    const zoomAndScalePosition =
      window.screen.width <= appConfig.tabletWidth
        ? "bottom-left"
        : "bottom-right";
    mView.ui.move(["zoom"], zoomAndScalePosition);
    mView.ui.add(
      new ScaleBar({
        view: mView,
        unit: "metric",
      }),
      { position: zoomAndScalePosition }
    );
    mView.ui.add(
      new Expand({
        view: mView,
        autoCollapse: true,
        content: new BasemapGallery({
          view: mView,
          source: basemaps,
        }),
      }),
      "top-right",
      1
    );
    const measurement = new Measurement({
      view: mView,
      linearUnit: "meters",
      container: "measure-widget-div",
    });
    mView.ui.add(measurement, "top-right");
    measurementWidgetRef.current = measurement;
  };

  const clearSearch = () => {
    searchWidget.clear();
    setSearchClearVisible(false);
  };

  const configureLoadingSpinner = (mView: any) => {
    mView.watch("updating", (updating: boolean) => {
      if (!updating) {
        // When layers update, often the spinner gets displayed and hidden quickly twice which is distracting
        // This timeout should prevent this occurring.
        mapSpinnerTimeout = setTimeout(() => {
          setMapViewUpdating(mapViewUpdating && updating);
        }, 200);
      } else {
        setMapViewUpdating(updating);
      }
    });
  };

  const configureSketchVm = (mView: any) => {
    const sketchVm = new SketchViewModel({
      view: mView,
      layer: pointGraphicsLayer,
      updateOnGraphicClick: false,
      defaultUpdateOptions: {
        enableRotation: true,
        enableZ: true,
        toggleToolOnClick: false,
        multipleSelectionEnabled: false,
        tool: "reshape",
      },
      pointSymbol: mapConfig.drawSymbols[treeType.Patch],
      polygonSymbol: mapConfig.drawSymbols.patch,
    });
    setSketchVm(sketchVm);
  };

  const initMap = () => {
    // in strict mode react will render twice so we need to avoid running this again
    if (mapRef.current) return;

    const basemaps = createBasemaps();
    mapRef.current = new Map({
      basemap: basemaps[0],
    });
    setMap(mapRef.current);

    sr = new SpatialReference({ wkid: mapConfig.sr });
    let mView = new MapView({
      container: "map",
      map: mapRef.current,
      spatialReference: sr,
      extent: new Extent({
        ...mapConfig.initialExtent,
        spatialReference: sr,
      }),
      constraints: {
        snapToZoom: false,
        minScale: mapConfig.minScale,
        maxScale: mapConfig.maxScale,
      },
    });
    mView.popup.autoOpenEnabled = false;
    mView.watch("stationary", () => {
      setMapScale(mView.scale);
    });
    setMapView(mView);
    configureLayers();
    addMapViewUiComponents(mView, basemaps);
    setSearchWidget(buildSearch("mapSearchWidgetInput", mView));
    configureSketchVm(mView);
    configureLoadingSpinner(mView);
  };

  const toggleLayerVisibility = (layerId: number) => {
    let lgdItems = [...legendItems];
    lgdItems.forEach((x) => {
      if (x.layerId === layerId) {
        x.visible = !x.visible;
      }
    });
    // One sublayer per legend item except for group layers like Contours
    const sublayers = getLegendItemSublayers(
      layerId,
      layersInfo.current.layers,
      mapRef.current
    );
    sublayers.forEach((x) => (x.visible = !x.visible));
    setLegendItems(lgdItems);
  };

  const updateOpacity = (layerId: number, opacity: number) => {
    const sublayers = getLegendItemSublayers(
      layerId,
      layersInfo.current.layers,
      mapRef.current
    );
    sublayers.forEach((x) => (x.opacity = opacity));
  };

  const zoomToGraphics = useCallback(() => {
    const graphicsToZoomTo = (polygonGraphicsLayer.graphics as any).items
      .concat((pointGraphicsLayer.graphics as any).items)
      .concat((treeBufferGraphicsLayer.graphics as any).items);
    if (!graphicsToZoomTo.length) return;
    const graphicsExtent = getGraphicsExtent(graphicsToZoomTo);
    mapView.goTo(graphicsExtent.expand(1.2), { duration: 800 });
  }, [
    polygonGraphicsLayer,
    pointGraphicsLayer,
    treeBufferGraphicsLayer,
    mapView,
  ]);

  const onLegendOpen = () => {
    if (
      [mapAdditionalModes.identify, mapAdditionalModes.measure].includes(
        mapMode as mapAdditionalModes
      )
    ) {
      setMapMode(undefined);
    }
    setIdentifyWindowOpen(false);
  };

  const openIdentifyWindow = () => {
    setMapMode(mapAdditionalModes.identify);
    setIdentifyWindowOpen(true);
  };

  const exitIdentifyMode = () => {
    setMapMode(undefined);
    setIdentifyWindowOpen(false);
  };

  const openMeasureWindow = () => {
    setMapMode(mapAdditionalModes.measure);
    setMeasureWindowOpen(true);
    measurementWidgetRef.current.activeTool = "distance";
    setTimeout(() => {
      const distance2dWidget: DistanceMeasurement2D =
        measurementWidgetRef.current.activeWidget;
      if (distance2dWidget) {
        distance2dWidget.viewModel.geodesicDistanceThreshold = 100;
      }
    }, 1000);
  };

  const stopMeasurementWidget = () => {
    measurementWidgetRef.current.clear();
    measurementWidgetRef.current.activeTool = undefined;
  };

  const closeMeasureWindow = () => {
    setMapMode(undefined);
    setMeasureWindowOpen(false);
    stopMeasurementWidget();
  };

  useEffect(() => {
    initMap();
    return () => clearTimeout(mapSpinnerTimeout);
    // eslint-disable-next-line
  }, []);

  // set up search
  useEffect(() => {
    // ensuring this only runs once
    searchWidget &&
      !searchKeyupListenerConfigured.current &&
      searchWidget.when(() => {
        const inputElement = document.getElementById(
          "mapSearchWidgetInput-input"
        ) as any;
        if (!inputElement) {
          return;
        }
        const esriHandler = inputElement.onkeyup;
        inputElement.onkeyup = (event: any) => {
          setSearchClearVisible(!!inputElement.value);
          esriHandler(event);
        };
        searchKeyupListenerConfigured.current = true;
      });
  }, [searchWidget, searchKeyupListenerConfigured]);

  // configure popup
  useEffect(() => {
    if (!mapView) return;
    clickHandlerRef.current?.remove();
    clickHandlerRef.current = mapView.on("click", (event: any) => {
      if (!identifiableLayer || mapMode !== mapAdditionalModes.identify) return;
      const layer = getMapSublayer(identifiableLayer.layerId, mapRef.current);
      layer
        .queryFeatures({
          geometry: event.mapPoint,
          spatialRelationship: "intersects",
          returnGeometry: false,
          outFields: ["*"],
        })
        .then((featureSet: any) => {
          const popupOptions: any = {
            location: event.mapPoint,
          };
          if (featureSet.features.length) {
            popupOptions.features = featureSet.features;
          } else {
            popupOptions.title = "Feature results";
            popupOptions.content = "No features found at this location";
          }
          mapView.popup.open(popupOptions);
          mapView.popup.set("dockOptions", { position: "bottom-center" });
        });
    });
  }, [clickHandlerRef, mapView, identifiableLayer, mapMode]);

  // close identify window when map mode changes to something else
  useEffect(() => {
    if (
      mapModeRef.current === mapAdditionalModes.identify &&
      mapMode !== mapAdditionalModes.identify
    ) {
      setIdentifyWindowOpen(false);
    }
    if (
      mapModeRef.current === mapAdditionalModes.measure &&
      mapMode !== mapAdditionalModes.measure
    ) {
      setMeasureWindowOpen(false);
      stopMeasurementWidget();
    }
  }, [mapMode, mapModeRef]);

  // close popup when identify window is closed
  useEffect(() => {
    if (!identifyWindowOpen && mapView?.popup.visible) {
      mapView.popup.close();
    }
  }, [identifyWindowOpen, mapView]);

  // update map from features state
  useEffect(() => {
    const drawPolygon = async (polyId: string, symbol: any) => {
      const feature = state.features[polyId];
      const polygon = new Polygon(feature.geom);
      const featureGraphic = new Graphic({
        geometry: polygon,
        attributes: { id: feature.id },
        symbol: symbol,
      });
      polygonGraphicsLayer.add(featureGraphic);
    };

    const drawTree = async (treeId: string) => {
      const feature = state.features[treeId] as Tree;
      const point = new Point(feature.geom);
      let treeSymbol = { ...mapConfig.drawSymbols[feature.type] };
      // Scattered trees in the offset tool are always represented as large trees as only
      // trees that are > 75% of the Large Tree Benchmark value are allowed to be mapped.
      treeSymbol.size =
        nvrToolMode === nvrTools.offset && feature.type === treeType.Scattered
          ? mapConfig.treeSizes[treeSize.Lg]
          : mapConfig.treeSizes[feature.size || treeSize.Sm];
      if (feature.benchmarkCm === undefined) {
        treeSymbol = { ...mapConfig.drawSymbols.flaggedTree };
      }
      const featureGraphic = new Graphic({
        geometry: point,
        attributes: { id: feature.id },
        symbol: treeSymbol,
      });
      pointGraphicsLayer.add(featureGraphic);
    };

    const drawPolygonLabel = async (polyId: string) => {
      const feature = state.features[polyId];
      const polygon = new Polygon(feature.geom);
      const labelGraphic = await getPolygonlabel(polygon, feature.id);
      labelGraphicsLayer.add(labelGraphic);
    };

    const drawTreeLabel = async (treeId: string) => {
      const feature = state.features[treeId] as Tree;
      const point = new Point(feature.geom);
      let labelColor = colours.treeLabels[feature.type];
      if (feature.benchmarkCm === undefined) {
        labelColor = colours.black;
      }
      const labelGraphic = getPointLabel(point, feature.id, labelColor);
      labelGraphicsLayer.add(labelGraphic);
    };

    const drawTreeBuffer = async (treeId: string) => {
      const feature = state.features[treeId] as Tree;
      const overlappingPatches = getOverlappingFeatures(
        feature.geom as Point,
        state.patchesList.map((x: string) => state.features[x] as Patch)
      );
      if (overlappingPatches.length) {
        return;
      }
      const bufferGraphic = await getPointBuffer(feature, nvrToolMode);
      treeBufferGraphicsLayer.add(bufferGraphic);
    };
    // don't redraw if the mapMode changes (only need to react to state changing). Except when entering/exiting edit mode
    if (
      mapModeRef.current !== mapMode &&
      ![mapModeRef.current, mapMode].includes(mapAdditionalModes.edit)
    )
      return;

    polygonGraphicsLayer.removeAll();
    pointGraphicsLayer.removeAll();
    labelGraphicsLayer.removeAll();
    treeBufferGraphicsLayer.removeAll();

    const promises: any[] = [
      ...state.patchesList.map((x) =>
        drawPolygon(x, mapConfig.drawSymbols.patch)
      ),
      ...state.revegetationAreasList.map((x) =>
        drawPolygon(x, mapConfig.drawSymbols.revegetationArea)
      ),
      ...state.treesList.map(drawTree),
      ...state.patchesList.map(drawPolygonLabel),
      ...state.revegetationAreasList.map(drawPolygonLabel),
      ...state.treesList.map(drawTreeLabel),
      ...state.treesList.map(drawTreeBuffer),
    ];

    Promise.all(promises).then(() => {
      if (mapMode === mapAdditionalModes.edit) {
        const patchPolys = state.patchesList.map(
          (x) =>
            new Graphic({
              geometry: new Polygon(state.features[x].geom),
              attributes: { id: x, type: featureTypes.patch },
              symbol: mapConfig.drawSymbols.editablePatch,
            })
        );
        const revegetationAreaPolys = state.revegetationAreasList.map(
          (x) =>
            new Graphic({
              geometry: new Polygon(state.features[x].geom),
              attributes: { id: x, type: featureTypes.revegetationArea },
              symbol: mapConfig.drawSymbols.editableRevegetationArea,
            })
        );
        polygonGraphicsLayer.removeAll();
        const pointGraphics = [...(pointGraphicsLayer.graphics as any).items];
        pointGraphicsLayer.graphics.removeAll();
        pointGraphicsLayer.addMany(patchPolys);
        pointGraphicsLayer.addMany(revegetationAreaPolys);
        pointGraphicsLayer.addMany(pointGraphics);
      }
      if (uploadingRef.current) {
        zoomToGraphics();
        setUploading(false);
      }
    });
  }, [
    state,
    polygonGraphicsLayer,
    pointGraphicsLayer,
    labelGraphicsLayer,
    treeBufferGraphicsLayer,
    zoomToGraphics,
    setUploading,
    mapMode,
    nvrToolMode,
  ]);

  // react to uploaded features
  useEffect(() => {
    uploadingRef.current = uploading;
  }, [uploading]);

  // Remember last map mode
  useEffect(() => {
    if (mapModeRef.current !== mapMode) {
      mapModeRef.current = mapMode;
    }
  }, [mapMode, mapModeRef]);

  return (
    <MapOuterContainer>
      <Search clearVisible={searchClearVisible} clearSearch={clearSearch} />
      <MapLegend
        items={legendItems}
        toggleLayerVisibility={toggleLayerVisibility}
        updateOpacity={updateOpacity}
        mapScale={mapScale}
        onOpen={onLegendOpen}
      />
      <IdentifyButton
        map={mapRef.current}
        legendItems={legendItems}
        identifiableLayer={identifiableLayer}
        setIdentifiableLayer={setIdentifiableLayer}
        mapScale={mapScale}
        open={identifyWindowOpen}
        setOpen={openIdentifyWindow}
        setClosed={exitIdentifyMode}
      />
      <MeasureButton
        open={measureWindowOpen}
        setOpen={openMeasureWindow}
        setClosed={closeMeasureWindow}
      />
      {mapMessage && <MapMessage text={mapMessage} type={mapMessageType} />}
      <MapDiv id="map">
        <MapLoadingSpinner visible={mapViewUpdating} />
      </MapDiv>
      <DialogMapContainer />
    </MapOuterContainer>
  );
}

export default MapContainer;
